import { useEffect, useState } from "react";
import { adminApi, getApiErrorMessage } from "@/lib/api";
import { AdminLayout } from "@/components/layout/AdminLayout";
import { Card, CardContent } from "@/components/ui/card";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { formatCurrency, formatDate } from "@/lib/format";
import { toast } from "sonner";
import { Database, Layers, Plus, RefreshCw, ShieldCheck, Upload } from "lucide-react";
import AifProviderAdmin, { AifMasterDialog } from "@/pages/admin/AifProviderAdmin";

const fmtPct = (v) => (v == null ? "—" : `${v}%`);

function Field({ label, children }) {
  return <div><label className="text-xs text-[var(--ink-muted)]">{label}</label>{children}</div>;
}

function Select({ value, onChange, options, testid }) {
  return (
    <select className="w-full rounded-md border border-[var(--glass-border)] bg-white p-2 text-sm" value={value} onChange={onChange} data-testid={testid}>
      {options.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
    </select>
  );
}

function RevenueStrip() {
  const [rev, setRev] = useState(null);
  useEffect(() => { adminApi.get("/admin/alternates/revenue").then((r) => setRev(r.data)); }, []);
  if (!rev) return null;
  return (
    <Card className="glass-card mb-6 border-0"><CardContent className="flex flex-wrap gap-8 p-5">
      {[["AIF trail / month", rev.aif_trail_monthly, "alt-rev-aif"],
        ["PMS trail / month", rev.pms_trail_monthly, "alt-rev-pms"],
        ["AIF setup fees earned", rev.aif_setup_fees, "alt-rev-setup"]].map(([l, v, tid]) => (
        <div key={l}>
          <div className="text-xs text-[var(--ink-muted)]">{l}</div>
          <div className="font-heading text-xl text-[var(--brand)]" data-testid={tid}>{formatCurrency(v)}</div>
        </div>
      ))}
    </CardContent></Card>
  );
}

function AifBook({ clients }) {
  const [book, setBook] = useState([]);
  const [sel, setSel] = useState(null);
  const [showNew, setShowNew] = useState(false);
  const [inv, setInv] = useState({ user_id: "", product_id: "", commitment_amount: "", setup_fee_earned: "", start_date: "" });
  const [products, setProducts] = useState([]);
  const [call, setCall] = useState({ amount: "", due_date: "", purpose: "" });
  const [nav, setNav] = useState({ date: "", nav_per_unit: "" });
  const [dist, setDist] = useState({ date: "", amount: "", dist_type: "INCOME" });

  const load = () => Promise.all([
    adminApi.get("/admin/alternates/aif/book").then((r) => setBook(r.data)),
    adminApi.get("/admin/alternates/aif/products").then((r) => setProducts(r.data)),
  ]);
  useEffect(() => { load(); }, []);
  const refreshSel = async (id) => { const { data } = await adminApi.get("/admin/alternates/aif/book"); setBook(data); setSel(data.find((x) => x.id === id) || null); };

  const act = (fn) => async () => {
    try { await fn(); toast.success("Done"); sel ? refreshSel(sel.id) : load(); }
    catch (err) { toast.error(getApiErrorMessage(err)); }
  };
  const createInv = act(async () => {
    await adminApi.post("/admin/alternates/aif/investments", { ...inv, commitment_amount: +inv.commitment_amount, setup_fee_earned: +inv.setup_fee_earned || 0 });
    setShowNew(false);
  });

  return (
    <>
      <div className="mb-3 flex justify-end">
        <Button className="rounded-full bg-[var(--cta)] hover:bg-[#087a56]" onClick={() => setShowNew(true)} data-testid="aif-new-investment-button"><Plus className="mr-1 h-4 w-4" /> New investment</Button>
      </div>
      <Card className="border-[var(--glass-border)]"><CardContent className="overflow-x-auto p-0">
        <table className="w-full text-sm" data-testid="aif-book-table">
          <thead><tr className="border-b border-[var(--glass-border)] text-left text-xs text-[var(--ink-muted)]">
            <th className="p-3 font-medium">Client / Fund</th><th className="p-3 text-right font-medium">Commitment</th><th className="p-3 text-right font-medium">Drawn</th><th className="p-3 text-right font-medium">Value</th><th className="p-3 text-right font-medium">TVPI</th><th className="p-3 text-right font-medium">XIRR</th><th className="p-3 text-right font-medium">Pending calls</th>
          </tr></thead>
          <tbody className="divide-y divide-[var(--glass-border)]">
            {book.map((b) => (
              <tr key={b.id} className="cursor-pointer hover:bg-[var(--surface-2)]" onClick={() => setSel(b)} data-testid={`aif-book-row-${b.id}`}>
                <td className="p-3"><div className="font-medium text-[var(--brand)]">{b.client?.full_name}</div><div className="text-xs text-[var(--ink-muted)]">{b.product?.name} · {b.product?.category?.replace("_", " ")}</div></td>
                <td className="p-3 text-right">{formatCurrency(b.commitment_amount)}</td>
                <td className="p-3 text-right">{formatCurrency(b.drawn)}</td>
                <td className="p-3 text-right font-medium">{formatCurrency(b.current_value)}</td>
                <td className="p-3 text-right">{b.tvpi ?? "—"}x</td>
                <td className="p-3 text-right">{fmtPct(b.net_xirr)}</td>
                <td className={`p-3 text-right ${b.pending_calls > 0 ? "font-medium text-amber-700" : ""}`}>{formatCurrency(b.pending_calls)}</td>
              </tr>
            ))}
            {book.length === 0 && <tr><td className="p-5 text-sm text-[var(--ink-muted)]" colSpan={7} data-testid="aif-empty-book">No AIF investments booked yet.</td></tr>}
          </tbody>
        </table>
      </CardContent></Card>

      <Dialog open={showNew} onOpenChange={setShowNew}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle className="font-heading text-[var(--brand)]">New AIF Investment</DialogTitle><DialogDescription className="text-xs">SEBI minimum commitment ₹1 Cr</DialogDescription></DialogHeader>
          <div className="space-y-3">
            <Field label="Client"><Select value={inv.user_id} onChange={(e) => setInv({ ...inv, user_id: e.target.value })} options={[["", "Select client"], ...clients.map((c) => [c.user_id, `${c.name} (${c.client_code || "—"})`])]} testid="aif-inv-client-select" /></Field>
            <Field label="AIF product"><Select value={inv.product_id} onChange={(e) => setInv({ ...inv, product_id: e.target.value })} options={[["", "Select product"], ...products.map((p) => [p.id, `${p.name} (${p.category.replace("_", " ")})`])]} testid="aif-inv-product-select" /></Field>
            <Field label="Commitment (₹)"><Input type="number" value={inv.commitment_amount} onChange={(e) => setInv({ ...inv, commitment_amount: e.target.value })} data-testid="aif-inv-amount-input" /></Field>
            <Field label="Setup fee earned by SMIFS (₹)"><Input type="number" value={inv.setup_fee_earned} onChange={(e) => setInv({ ...inv, setup_fee_earned: e.target.value })} /></Field>
            <Field label="Start date"><Input type="date" value={inv.start_date} onChange={(e) => setInv({ ...inv, start_date: e.target.value })} data-testid="aif-inv-date-input" /></Field>
            <Button className="w-full rounded-full bg-[var(--cta)] hover:bg-[#087a56]" onClick={createInv} disabled={!inv.user_id || !inv.product_id || !inv.commitment_amount || !inv.start_date} data-testid="aif-inv-save-button">Book investment</Button>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={!!sel} onOpenChange={(o) => !o && setSel(null)}>
        <DialogContent className="max-h-[85vh] max-w-2xl overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-heading text-[var(--brand)]">{sel?.client?.full_name} — {sel?.product?.name}</DialogTitle>
            <DialogDescription className="text-xs">Committed {formatCurrency(sel?.commitment_amount)} · Drawn {formatCurrency(sel?.drawn)} · Undrawn {formatCurrency(sel?.undrawn)} · Value {formatCurrency(sel?.current_value)} · NAV ₹{sel?.latest_nav}</DialogDescription>
          </DialogHeader>
          {sel && (
            <div className="space-y-4 text-sm">
              <div className="divide-y divide-[var(--glass-border)] rounded-xl border border-[var(--glass-border)]">
                {sel.calls.map((c) => (
                  <div key={c.id} className="flex items-center justify-between px-3 py-2 text-xs">
                    <div><b>Call #{c.call_no}</b> · {formatCurrency(c.amount)} · due {formatDate(c.due_date)} · <span className={c.status === "PAID" ? "text-emerald-700" : "text-amber-700"}>{c.status}</span></div>
                    {c.status !== "PAID" && <Button size="sm" variant="outline" className="h-7 rounded-full text-xs" onClick={act(async () => { await adminApi.post(`/admin/alternates/aif/capital-calls/${c.id}/mark-paid`, {}); })} data-testid={`aif-mark-paid-${c.id}`}>Mark paid</Button>}
                  </div>
                ))}
                {sel.calls.length === 0 && <div className="p-3 text-xs text-[var(--ink-muted)]">No capital calls yet.</div>}
              </div>
              <div className="grid gap-3 sm:grid-cols-3">
                <div className="space-y-2 rounded-xl border border-[var(--glass-border)] p-3">
                  <div className="text-xs font-medium text-[var(--brand)]">Raise capital call</div>
                  <Input type="number" placeholder="Amount ₹" value={call.amount} onChange={(e) => setCall({ ...call, amount: e.target.value })} data-testid="aif-call-amount-input" />
                  <Input type="date" value={call.due_date} onChange={(e) => setCall({ ...call, due_date: e.target.value })} data-testid="aif-call-due-input" />
                  <Input placeholder="Purpose" value={call.purpose} onChange={(e) => setCall({ ...call, purpose: e.target.value })} />
                  <Button size="sm" className="w-full rounded-full bg-[var(--cta)] hover:bg-[#087a56]" disabled={!call.amount || !call.due_date} onClick={act(async () => { await adminApi.post("/admin/alternates/aif/capital-calls", { investment_id: sel.id, amount: +call.amount, due_date: call.due_date, purpose: call.purpose || "Investment drawdown" }); setCall({ amount: "", due_date: "", purpose: "" }); })} data-testid="aif-call-save-button">Raise call</Button>
                </div>
                <div className="space-y-2 rounded-xl border border-[var(--glass-border)] p-3">
                  <div className="text-xs font-medium text-[var(--brand)]">Post fund NAV</div>
                  <Input type="date" value={nav.date} onChange={(e) => setNav({ ...nav, date: e.target.value })} data-testid="aif-nav-date-input" />
                  <Input type="number" placeholder="NAV per unit" value={nav.nav_per_unit} onChange={(e) => setNav({ ...nav, nav_per_unit: e.target.value })} data-testid="aif-nav-value-input" />
                  <Button size="sm" className="w-full rounded-full bg-[var(--cta)] hover:bg-[#087a56]" disabled={!nav.date || !nav.nav_per_unit} onClick={act(async () => { await adminApi.post("/admin/alternates/aif/valuations", { product_id: sel.product_id, date: nav.date, nav_per_unit: +nav.nav_per_unit }); setNav({ date: "", nav_per_unit: "" }); })} data-testid="aif-nav-save-button">Post NAV</Button>
                </div>
                <div className="space-y-2 rounded-xl border border-[var(--glass-border)] p-3">
                  <div className="text-xs font-medium text-[var(--brand)]">Record distribution</div>
                  <Input type="date" value={dist.date} onChange={(e) => setDist({ ...dist, date: e.target.value })} data-testid="aif-dist-date-input" />
                  <Input type="number" placeholder="Amount ₹" value={dist.amount} onChange={(e) => setDist({ ...dist, amount: e.target.value })} data-testid="aif-dist-amount-input" />
                  <Select value={dist.dist_type} onChange={(e) => setDist({ ...dist, dist_type: e.target.value })} options={[["INCOME", "Income"], ["CAPITAL_RETURN", "Capital return"]]} />
                  <Button size="sm" className="w-full rounded-full bg-[var(--cta)] hover:bg-[#087a56]" disabled={!dist.date || !dist.amount} onClick={act(async () => { await adminApi.post("/admin/alternates/aif/distributions", { investment_id: sel.id, date: dist.date, amount: +dist.amount, dist_type: dist.dist_type }); setDist({ date: "", amount: "", dist_type: "INCOME" }); })} data-testid="aif-dist-save-button">Record</Button>
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}

function AifProducts() {
  const [products, setProducts] = useState([]);
  const [show, setShow] = useState(false);
  const [editing, setEditing] = useState(null);
  const load = () => adminApi.get("/admin/alternates/aif/products").then((r) => setProducts(r.data));
  useEffect(() => { load(); }, []);
  return (
    <>
      <div className="mb-3 flex justify-end"><Button className="rounded-full bg-[var(--cta)] hover:bg-[#087a56]" onClick={() => { setEditing(null); setShow(true); }} data-testid="aif-new-product-button"><Plus className="mr-1 h-4 w-4" /> Add AIF master manually</Button></div>
      <Card className="border-[var(--glass-border)]"><CardContent className="overflow-x-auto p-0">
        <table className="w-full text-sm" data-testid="aif-products-table">
          <thead><tr className="border-b border-[var(--glass-border)] text-left text-xs text-[var(--ink-muted)]">
            <th className="p-3 font-medium">Fund</th><th className="p-3 font-medium">Category</th><th className="p-3 font-medium">Structure</th><th className="p-3 text-right font-medium">Min commit</th><th className="p-3 text-right font-medium">Mgmt / Carry / Hurdle</th><th className="p-3 font-medium">Source / version</th><th className="p-3 text-right font-medium">Action</th>
          </tr></thead>
          <tbody className="divide-y divide-[var(--glass-border)]">
            {products.map((p) => (
              <tr key={p.id}>
                <td className="p-3"><div className="font-medium text-[var(--brand)]">{p.name}</div><div className="text-xs text-[var(--ink-muted)]">{p.manager} · {p.sub_type?.replace("_", " ")}</div></td>
                <td className="p-3">{p.category?.replace("_", " ")}</td>
                <td className="p-3 text-xs">{p.structure === "CLOSED" ? `Close-ended ${p.tenure_years}y` : "Open-ended"} · NAV {p.nav_frequency?.toLowerCase()}</td>
                <td className="p-3 text-right">{formatCurrency(p.min_commitment)}</td>
                <td className="p-3 text-right text-xs">{p.mgmt_fee_pct}% / {p.carry_pct}% / {p.hurdle_pct}%</td>
                <td className="p-3 text-xs">{p.source?.replaceAll("_", " ") || "Legacy manual"} · v{p.master_version || 1}</td>
                <td className="p-3 text-right"><Button size="sm" variant="outline" onClick={() => { setEditing(p); setShow(true); }}>Edit</Button></td>
              </tr>
            ))}
            {products.length === 0 && <tr><td colSpan={7} className="p-6 text-center text-[var(--ink-muted)]">No AIF master records yet.</td></tr>}
          </tbody>
        </table>
      </CardContent></Card>
      <AifMasterDialog open={show} onOpenChange={setShow} product={editing} onSaved={load} />
    </>
  );
}

function PmsBook({ clients }) {
  const [book, setBook] = useState([]);
  const [sel, setSel] = useState(null);
  const [showNew, setShowNew] = useState(false);
  const [strategies, setStrategies] = useState([]);
  const [acc, setAcc] = useState({ user_id: "", strategy_id: "", inception_date: "", initial_capital: "" });
  const [val, setVal] = useState({ date: "", portfolio_value: "" });
  const [flow, setFlow] = useState({ date: "", amount: "", direction: "IN" });

  const load = () => Promise.all([
    adminApi.get("/admin/alternates/pms/book").then((r) => setBook(r.data)),
    adminApi.get("/admin/alternates/pms/strategies").then((r) => setStrategies(r.data)),
  ]);
  useEffect(() => { load(); }, []);
  const refreshSel = async (id) => { const { data } = await adminApi.get("/admin/alternates/pms/book"); setBook(data); setSel(data.find((x) => x.id === id) || null); };
  const act = (fn) => async () => {
    try { await fn(); toast.success("Done"); sel ? refreshSel(sel.id) : load(); }
    catch (err) { toast.error(getApiErrorMessage(err)); }
  };
  const uploadHoldings = async (e) => {
    const file = e.target.files?.[0];
    if (!file || !sel) return;
    try {
      const text = await file.text();
      const { data } = await adminApi.post(`/admin/alternates/pms/accounts/${sel.id}/holdings-upload`, { file_name: file.name, file_content: text });
      toast.success(`${data.holdings} holdings imported${data.skipped ? ` (${data.skipped} rows skipped — bad numbers)` : ""}`);
      refreshSel(sel.id);
    } catch (err) { toast.error(getApiErrorMessage(err)); }
    e.target.value = "";
  };

  return (
    <>
      <div className="mb-3 flex justify-end"><Button className="rounded-full bg-[var(--cta)] hover:bg-[#087a56]" onClick={() => setShowNew(true)} data-testid="pms-new-account-button"><Plus className="mr-1 h-4 w-4" /> New PMS account</Button></div>
      <Card className="border-[var(--glass-border)]"><CardContent className="overflow-x-auto p-0">
        <table className="w-full text-sm" data-testid="pms-book-table">
          <thead><tr className="border-b border-[var(--glass-border)] text-left text-xs text-[var(--ink-muted)]">
            <th className="p-3 font-medium">Client / Strategy</th><th className="p-3 text-right font-medium">Capital In</th><th className="p-3 text-right font-medium">Value</th><th className="p-3 text-right font-medium">Gain</th><th className="p-3 text-right font-medium">XIRR</th><th className="p-3 text-right font-medium">Est. fees /yr</th>
          </tr></thead>
          <tbody className="divide-y divide-[var(--glass-border)]">
            {book.map((b) => (
              <tr key={b.id} className="cursor-pointer hover:bg-[var(--surface-2)]" onClick={() => setSel(b)} data-testid={`pms-book-row-${b.id}`}>
                <td className="p-3"><div className="font-medium text-[var(--brand)]">{b.client?.full_name}</div><div className="text-xs text-[var(--ink-muted)]">{b.strategy?.name} · {b.strategy?.type?.replace("_", "-")}</div></td>
                <td className="p-3 text-right">{formatCurrency(b.capital_in_total)}</td>
                <td className="p-3 text-right font-medium">{formatCurrency(b.current_value)}</td>
                <td className={`p-3 text-right ${b.gain >= 0 ? "text-emerald-700" : "text-red-600"}`}>{fmtPct(b.gain_pct)}</td>
                <td className="p-3 text-right">{fmtPct(b.xirr)}</td>
                <td className="p-3 text-right">{formatCurrency(b.fee_estimate?.total_annual)}</td>
              </tr>
            ))}
            {book.length === 0 && <tr><td className="p-5 text-sm text-[var(--ink-muted)]" colSpan={6} data-testid="pms-empty-book">No PMS accounts yet.</td></tr>}
          </tbody>
        </table>
      </CardContent></Card>

      <Dialog open={showNew} onOpenChange={setShowNew}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle className="font-heading text-[var(--brand)]">New PMS Account</DialogTitle><DialogDescription className="text-xs">SEBI minimum ₹50 lakh</DialogDescription></DialogHeader>
          <div className="space-y-3">
            <Field label="Client"><Select value={acc.user_id} onChange={(e) => setAcc({ ...acc, user_id: e.target.value })} options={[["", "Select client"], ...clients.map((c) => [c.user_id, `${c.name} (${c.client_code || "—"})`])]} testid="pms-acc-client-select" /></Field>
            <Field label="Strategy"><Select value={acc.strategy_id} onChange={(e) => setAcc({ ...acc, strategy_id: e.target.value })} options={[["", "Select strategy"], ...strategies.map((s) => [s.id, `${s.name} (${s.type.replace("_", "-")})`])]} testid="pms-acc-strategy-select" /></Field>
            <Field label="Inception date"><Input type="date" value={acc.inception_date} onChange={(e) => setAcc({ ...acc, inception_date: e.target.value })} data-testid="pms-acc-date-input" /></Field>
            <Field label="Initial capital (₹)"><Input type="number" value={acc.initial_capital} onChange={(e) => setAcc({ ...acc, initial_capital: e.target.value })} data-testid="pms-acc-capital-input" /></Field>
            <Button className="w-full rounded-full bg-[var(--cta)] hover:bg-[#087a56]" onClick={act(async () => { await adminApi.post("/admin/alternates/pms/accounts", { ...acc, initial_capital: +acc.initial_capital }); setShowNew(false); })} disabled={!acc.user_id || !acc.strategy_id || !acc.inception_date || !acc.initial_capital} data-testid="pms-acc-save-button">Open account</Button>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={!!sel} onOpenChange={(o) => !o && setSel(null)}>
        <DialogContent className="max-h-[85vh] max-w-2xl overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-heading text-[var(--brand)]">{sel?.client?.full_name} — {sel?.strategy?.name}</DialogTitle>
            <DialogDescription className="text-xs">A/c {sel?.account_no} · Value {formatCurrency(sel?.current_value)} · HWM {formatCurrency(sel?.high_water_mark)} · {sel?.holdings?.length} holdings</DialogDescription>
          </DialogHeader>
          {sel && (
            <div className="space-y-4 text-sm">
              <div className="grid gap-3 sm:grid-cols-3">
                <div className="space-y-2 rounded-xl border border-[var(--glass-border)] p-3">
                  <div className="text-xs font-medium text-[var(--brand)]">Post valuation</div>
                  <Input type="date" value={val.date} onChange={(e) => setVal({ ...val, date: e.target.value })} data-testid="pms-val-date-input" />
                  <Input type="number" placeholder="Portfolio value ₹" value={val.portfolio_value} onChange={(e) => setVal({ ...val, portfolio_value: e.target.value })} data-testid="pms-val-value-input" />
                  <Button size="sm" className="w-full rounded-full bg-[var(--cta)] hover:bg-[#087a56]" disabled={!val.date || !val.portfolio_value} onClick={act(async () => { await adminApi.post("/admin/alternates/pms/valuations", { account_id: sel.id, date: val.date, portfolio_value: +val.portfolio_value }); setVal({ date: "", portfolio_value: "" }); })} data-testid="pms-val-save-button">Post</Button>
                </div>
                <div className="space-y-2 rounded-xl border border-[var(--glass-border)] p-3">
                  <div className="text-xs font-medium text-[var(--brand)]">Capital flow</div>
                  <Input type="date" value={flow.date} onChange={(e) => setFlow({ ...flow, date: e.target.value })} />
                  <Input type="number" placeholder="Amount ₹" value={flow.amount} onChange={(e) => setFlow({ ...flow, amount: e.target.value })} />
                  <Select value={flow.direction} onChange={(e) => setFlow({ ...flow, direction: e.target.value })} options={[["IN", "Top-up (in)"], ["OUT", "Withdrawal (out)"]]} />
                  <Button size="sm" className="w-full rounded-full bg-[var(--cta)] hover:bg-[#087a56]" disabled={!flow.date || !flow.amount} onClick={act(async () => { await adminApi.post(`/admin/alternates/pms/accounts/${sel.id}/flows`, { ...flow, amount: +flow.amount }); setFlow({ date: "", amount: "", direction: "IN" }); })}>Record</Button>
                </div>
                <div className="space-y-2 rounded-xl border border-[var(--glass-border)] p-3">
                  <div className="text-xs font-medium text-[var(--brand)]">Custodian holdings (CSV)</div>
                  <p className="text-[10px] text-[var(--ink-muted)]">SYMBOL,NAME,SECTOR,QUANTITY,AVG_COST,LAST_PRICE</p>
                  <label className="flex cursor-pointer items-center justify-center gap-1.5 rounded-full bg-[var(--cta)] px-3 py-1.5 text-xs font-medium text-white">
                    <Upload className="h-3.5 w-3.5" /> Upload file
                    <input type="file" accept=".csv,.txt" className="hidden" onChange={uploadHoldings} data-testid="pms-holdings-upload-input" />
                  </label>
                </div>
              </div>
              <div className="divide-y divide-[var(--glass-border)] rounded-xl border border-[var(--glass-border)]">
                {sel.holdings.slice(0, 15).map((h) => (
                  <div key={h.symbol} className="flex justify-between px-3 py-1.5 text-xs">
                    <span><b>{h.symbol}</b> · {h.sector}</span>
                    <span>{h.quantity} × ₹{h.last_price} = {formatCurrency(h.market_value)}</span>
                  </div>
                ))}
                {sel.holdings.length === 0 && <div className="p-3 text-xs text-[var(--ink-muted)]">No holdings uploaded yet.</div>}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}

function PmsStrategies() {
  const [strategies, setStrategies] = useState([]);
  const [master, setMaster] = useState(null);
  const [show, setShow] = useState(false);
  const [importing, setImporting] = useState(false);
  const [rightsConfirmed, setRightsConfirmed] = useState(false);
  const [authorizationReference, setAuthorizationReference] = useState("");
  const [f, setF] = useState({ name: "", manager: "SMIFS PMS Desk", type: "DISCRETIONARY", style: "", benchmark: "Nifty 50 TRI", min_investment: "5000000", fee_model: "HYBRID", fixed_fee_pct: "1.5", perf_fee_pct: "15", hurdle_pct: "10", high_water_mark: true, distributor_trail_bps: "100" });
  const load = () => Promise.all([
    adminApi.get("/admin/alternates/pms/strategies").then((r) => setStrategies(r.data)),
    adminApi.get("/admin/alternates/pms/master-status").then((r) => setMaster(r.data)),
  ]);
  useEffect(() => { load(); }, []);
  const save = async () => {
    try {
      await adminApi.post("/admin/alternates/pms/strategies", { ...f, min_investment: +f.min_investment, fixed_fee_pct: +f.fixed_fee_pct, perf_fee_pct: +f.perf_fee_pct, hurdle_pct: +f.hurdle_pct, distributor_trail_bps: +f.distributor_trail_bps });
      toast.success("Strategy created"); setShow(false); load();
    } catch (err) { toast.error(getApiErrorMessage(err)); }
  };
  const uploadMaster = async (event) => {
    const input = event.target;
    const file = input.files?.[0];
    if (!file) return;
    if (!rightsConfirmed) {
      toast.error("Confirm SMIFS's APMI data-use authorization before importing.");
      input.value = "";
      return;
    }
    if (!authorizationReference.trim()) {
      toast.error("Enter the written permission, licence, or authorized feed reference.");
      input.value = "";
      return;
    }
    if (file.size > 12 * 1024 * 1024) {
      toast.error("The APMI import file must be 12 MB or smaller.");
      input.value = "";
      return;
    }
    setImporting(true);
    try {
      const fileContent = await file.text();
      const { data } = await adminApi.post("/admin/alternates/pms/master-import", {
        file_name: file.name,
        file_content: fileContent,
        rights_confirmed: true,
        authorization_reference: authorizationReference.trim(),
      });
      toast.success(data.already_imported
        ? "This authorized APMI snapshot was already imported."
        : `APMI master updated: ${data.strategies_imported} approaches, ${data.providers_mapped} providers.`);
      await load();
    } catch (err) {
      toast.error(getApiErrorMessage(err));
    } finally {
      setImporting(false);
      input.value = "";
    }
  };
  const syncFeed = async () => {
    setImporting(true);
    try {
      const { data } = await adminApi.post("/admin/alternates/pms/master-sync");
      toast.success(data.already_imported
        ? "Authorized APMI feed is already current."
        : `Authorized APMI feed synchronized: ${data.strategies_imported} approaches.`);
      await load();
    } catch (err) {
      toast.error(getApiErrorMessage(err));
    } finally {
      setImporting(false);
    }
  };
  return (
    <>
      <Card className="mb-4 border-[var(--glass-border)]" data-testid="pms-apmi-master-card">
        <CardContent className="space-y-4 p-4">
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div>
              <div className="flex items-center gap-2 font-medium text-[var(--brand)]"><Database className="h-4 w-4" /> PMS research master · APMI</div>
              <p className="mt-1 max-w-3xl text-xs text-[var(--ink-muted)]">
                Official provider, investment-approach, AUM, benchmark and reported-performance fields are mapped into research. SMIFS fees, trail, minimums and client account data are preserved separately.
              </p>
            </div>
            <div className="grid grid-cols-2 gap-4 text-right text-xs">
              <div><div className="text-[var(--ink-muted)]">Approaches</div><div className="font-heading text-lg text-[var(--brand)]" data-testid="pms-master-strategy-count">{master?.strategies ?? "—"}</div></div>
              <div><div className="text-[var(--ink-muted)]">Providers</div><div className="font-heading text-lg text-[var(--brand)]" data-testid="pms-master-provider-count">{master?.providers ?? "—"}</div></div>
            </div>
          </div>
          <div className={`rounded-lg border p-3 text-xs ${master?.dashboard_sync_enabled ? "border-emerald-200 bg-emerald-50 text-emerald-950" : "border-amber-200 bg-amber-50 text-amber-950"}`}>
            {master?.dashboard_sync_enabled
              ? "Authorized full APMI connector is active. Provider master and all investment-approach performance periods refresh automatically."
              : "Automatic APMI sync is not configured. Import only an export/feed that SMIFS is authorized to store and republish."}
            {master?.latest_import?.completed_at && <span> Last successful import: {formatDate(master.latest_import.completed_at)} ({master.latest_import.strategies_imported} approaches).</span>}
            {master?.research?.computed_at && <span> Research rebuilt: {formatDate(master.research.computed_at)} ({master.research.rated} rated).</span>}
            {master?.schedule?.next_due_at && <span> Next refresh due: {formatDate(master.schedule.next_due_at)}.</span>}
            {master?.sync_state?.state === "FAILED" && <span className="block font-medium text-red-700">Last sync failed: {master.sync_state.error}</span>}
          </div>
          <div className="grid gap-3 lg:grid-cols-[1fr_auto] lg:items-end">
            <div className="space-y-2">
              <Input
                value={authorizationReference}
                onChange={(e) => setAuthorizationReference(e.target.value)}
                placeholder="Authorization / licence / feed agreement reference"
                data-testid="pms-master-authorization-input"
              />
              <label className="flex items-start gap-2 text-xs text-[var(--ink-muted)]">
                <input className="mt-0.5" type="checkbox" checked={rightsConfirmed} onChange={(e) => setRightsConfirmed(e.target.checked)} data-testid="pms-master-rights-checkbox" />
                <span><ShieldCheck className="mr-1 inline h-3.5 w-3.5 text-emerald-700" />I confirm SMIFS has written permission or licensed feed rights for this APMI data.</span>
              </label>
            </div>
            <div className="flex flex-wrap gap-2">
              {master?.connector_enabled && (
                <Button variant="outline" className="rounded-full" disabled={importing} onClick={syncFeed} data-testid="pms-master-sync-button">
                  <RefreshCw className={`mr-1.5 h-4 w-4 ${importing ? "animate-spin" : ""}`} /> Sync authorized feed
                </Button>
              )}
              <label className={`inline-flex items-center rounded-full bg-[var(--cta)] px-4 py-2 text-sm font-medium text-white ${importing ? "cursor-not-allowed opacity-60" : "cursor-pointer hover:bg-[#087a56]"}`}>
                <Upload className="mr-1.5 h-4 w-4" /> {importing ? "Importing…" : "Import APMI CSV / JSON"}
                <input type="file" accept=".csv,.json,text/csv,application/json" className="hidden" disabled={importing} onChange={uploadMaster} data-testid="pms-master-file-input" />
              </label>
              <Button className="rounded-full bg-[var(--brand)] hover:bg-[var(--brand)]/90" onClick={() => setShow(true)} data-testid="pms-new-strategy-button"><Plus className="mr-1 h-4 w-4" /> Local strategy</Button>
            </div>
          </div>
        </CardContent>
      </Card>
      <Card className="border-[var(--glass-border)]"><CardContent className="overflow-x-auto p-0">
        <table className="w-full text-sm" data-testid="pms-strategies-table">
          <thead><tr className="border-b border-[var(--glass-border)] text-left text-xs text-[var(--ink-muted)]">
            <th className="p-3 font-medium">Investment approach</th><th className="p-3 font-medium">Source / Type</th><th className="p-3 text-right font-medium">AUM</th><th className="p-3 text-right font-medium">1Y / 3Y</th><th className="p-3 font-medium">Commercial setup</th><th className="p-3 text-right font-medium">Min invest</th>
          </tr></thead>
          <tbody className="divide-y divide-[var(--glass-border)]">
            {strategies.map((s) => (
              <tr key={s.id}>
                <td className="p-3"><div className="font-medium text-[var(--brand)]">{s.name}</div><div className="text-xs text-[var(--ink-muted)]">{s.manager} · {s.category || s.style} · vs {s.benchmark}</div></td>
                <td className="p-3 text-xs"><div>{s.source || "LOCAL"}</div><div className="text-[var(--ink-muted)]">{(s.type || "UNSPECIFIED").replaceAll("_", "-")}</div></td>
                <td className="p-3 text-right text-xs">{s.aum_crore != null ? `₹${Number(s.aum_crore).toLocaleString("en-IN", { maximumFractionDigits: 2 })} Cr` : "—"}</td>
                <td className="p-3 text-right text-xs">{fmtPct(s.returns?.["1y"])} / {fmtPct(s.returns?.["3y"])}<div className="text-[10px] text-[var(--ink-muted)]">{s.performance_as_of ? `as of ${formatDate(s.performance_as_of)}` : ""}</div></td>
                <td className="p-3 text-xs">{s.fee_model === "NOT_CONFIGURED" ? <span className="text-amber-700">Not configured</span> : s.fee_model === "FIXED" ? `${s.fixed_fee_pct}% fixed` : s.fee_model === "PERFORMANCE" ? `${s.perf_fee_pct}% > ${s.hurdle_pct}%` : `${s.fixed_fee_pct}% + ${s.perf_fee_pct}% > ${s.hurdle_pct}%`}{s.high_water_mark ? " · HWM" : ""}</td>
                <td className="p-3 text-right">{formatCurrency(s.min_investment)}</td>
              </tr>
            ))}
            {strategies.length === 0 && <tr><td colSpan={6} className="p-5 text-sm text-[var(--ink-muted)]">No PMS strategies have been configured or imported.</td></tr>}
          </tbody>
        </table>
      </CardContent></Card>
      <Dialog open={show} onOpenChange={setShow}>
        <DialogContent className="max-h-[85vh] max-w-lg overflow-y-auto">
          <DialogHeader><DialogTitle className="font-heading text-[var(--brand)]">New PMS Strategy</DialogTitle><DialogDescription className="text-xs">Discretionary / Non-discretionary / Advisory</DialogDescription></DialogHeader>
          <div className="grid grid-cols-2 gap-3">
            <div className="col-span-2"><Field label="Strategy name"><Input value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} data-testid="pmss-name-input" /></Field></div>
            <Field label="Type"><Select value={f.type} onChange={(e) => setF({ ...f, type: e.target.value })} options={[["DISCRETIONARY", "Discretionary"], ["NON_DISCRETIONARY", "Non-discretionary"], ["ADVISORY", "Advisory"]]} testid="pmss-type-select" /></Field>
            <Field label="Style"><Input value={f.style} onChange={(e) => setF({ ...f, style: e.target.value })} placeholder="e.g. Small-Cap Quality" /></Field>
            <Field label="Benchmark"><Input value={f.benchmark} onChange={(e) => setF({ ...f, benchmark: e.target.value })} /></Field>
            <Field label="Min investment ₹ (≥50L)"><Input type="number" value={f.min_investment} onChange={(e) => setF({ ...f, min_investment: e.target.value })} /></Field>
            <Field label="Fee model"><Select value={f.fee_model} onChange={(e) => setF({ ...f, fee_model: e.target.value })} options={[["FIXED", "Fixed only"], ["PERFORMANCE", "Performance only"], ["HYBRID", "Hybrid"]]} testid="pmss-fee-select" /></Field>
            <Field label="Fixed fee %"><Input type="number" value={f.fixed_fee_pct} onChange={(e) => setF({ ...f, fixed_fee_pct: e.target.value })} /></Field>
            <Field label="Performance fee %"><Input type="number" value={f.perf_fee_pct} onChange={(e) => setF({ ...f, perf_fee_pct: e.target.value })} /></Field>
            <Field label="Hurdle %"><Input type="number" value={f.hurdle_pct} onChange={(e) => setF({ ...f, hurdle_pct: e.target.value })} /></Field>
            <Field label="Distributor trail (bps)"><Input type="number" value={f.distributor_trail_bps} onChange={(e) => setF({ ...f, distributor_trail_bps: e.target.value })} /></Field>
            <label className="col-span-2 flex items-center gap-2 text-xs"><input type="checkbox" checked={f.high_water_mark} onChange={(e) => setF({ ...f, high_water_mark: e.target.checked })} /> High-water mark applies to performance fee</label>
          </div>
          <Button className="w-full rounded-full bg-[var(--cta)] hover:bg-[#087a56]" onClick={save} disabled={!f.name} data-testid="pmss-save-button">Create strategy</Button>
        </DialogContent>
      </Dialog>
    </>
  );
}

export default function AlternatesAdmin() {
  const [clients, setClients] = useState([]);
  useEffect(() => { adminApi.get("/admin/crm/clients").then((r) => setClients(r.data)); }, []);
  return (
    <AdminLayout>
      <div className="mb-6">
        <h1 className="flex items-center gap-2 font-heading text-3xl text-[var(--brand)]"><Layers className="h-7 w-7" /> AIF & PMS</h1>
        <p className="mt-1 text-sm text-[var(--ink-muted)]">Alternates back office — products, commitments, capital calls, NAVs, custodian holdings and revenue.</p>
      </div>
      <RevenueStrip />
      <Tabs defaultValue="aif-book">
        <TabsList>
          <TabsTrigger value="aif-book" data-testid="alt-tab-aif-book">AIF Book</TabsTrigger>
          <TabsTrigger value="aif-products" data-testid="alt-tab-aif-products">AIF Products</TabsTrigger>
          <TabsTrigger value="aif-providers" data-testid="alt-tab-aif-providers">AIF Providers</TabsTrigger>
          <TabsTrigger value="pms-book" data-testid="alt-tab-pms-book">PMS Book</TabsTrigger>
          <TabsTrigger value="pms-strategies" data-testid="alt-tab-pms-strategies">PMS Strategies</TabsTrigger>
        </TabsList>
        <TabsContent value="aif-book"><AifBook clients={clients} /></TabsContent>
        <TabsContent value="aif-products"><AifProducts /></TabsContent>
        <TabsContent value="aif-providers"><AifProviderAdmin /></TabsContent>
        <TabsContent value="pms-book"><PmsBook clients={clients} /></TabsContent>
        <TabsContent value="pms-strategies"><PmsStrategies /></TabsContent>
      </Tabs>
    </AdminLayout>
  );
}
